import { useEffect, useRef, useState } from 'react'
import { FullKeyButton } from '../../../components/fullKeyButton/FullKeyButton'
import { translations } from '../../../configs/translations'
import { AnyModuleCharacteristics, ModuleEffect, ModuleTypes } from '../../../models/VehicleModule'
import { useStore } from '../../../store/store'
import { resolveTranslationKeyForValueSignBySpecType, VehicleSpecParam } from '../../main/vehicleSpec/VehicleSpec.types'
import './ModuleSpec.css'

export interface ModuleSpecProps {
	name: string
	moduleKey: string
	type: ModuleTypes
	level: number
	icon: string
	price: number
	xpPrice: number
	isResearched: boolean
	isBought: boolean
	isEquipped: boolean
	technicalCharacteristics: AnyModuleCharacteristics
	effects: ModuleEffect[]
}

export const ModuleSpec = ({ name, moduleKey, type, level, icon, price, xpPrice, technicalCharacteristics, isResearched, isBought, isEquipped, effects }: ModuleSpecProps) => {

	const language = useStore(state => state.language) as keyof typeof translations
	const t = translations[language]

	const [isAltHolded, setIsAltHolded] = useState(false)
	const altHeldRef = useRef(false)

	const resolveTypeTranslation = () => {
		switch (type) {
			case 'ENGINE': return t.engine
			case 'BRAKES': return t.brakes
			case 'TRANSMISSION': return t.transmission
			case 'TURBO': return t.turbo
			case 'TYRES': return t.tyre
			default: return t.engine
		}
	}

	console.log("HERE", technicalCharacteristics.resource)

	useEffect(() => {
		const onDown = (e: KeyboardEvent) => {
			if (e.key !== 'Alt') return
			altHeldRef.current = true
			setIsAltHolded(true)
		}
		const onUp = (e: KeyboardEvent) => {
			if (e.key !== 'Alt') return
			altHeldRef.current = false
			requestAnimationFrame(() => {
				if (!altHeldRef.current) setIsAltHolded(false)
			})
		}
		const onBlur = () => {
			altHeldRef.current = false
			setIsAltHolded(false)
		}

		window.addEventListener('keydown', onDown)
		window.addEventListener('keyup', onUp)
		window.addEventListener('blur', onBlur)

		return () => {
			window.removeEventListener('keydown', onDown)
			window.removeEventListener('keyup', onUp)
			window.removeEventListener('blur', onBlur)
		}
	}, [])

	const resolveTechnicalValue = (charType: string, value: string) => {
		switch (charType) {
			case 'type': return t[`${type.toLowerCase()}_${value.toLowerCase()}` as keyof typeof t] ?? t.unknown
			case 'volume': return `${value} ${t.centimeters_cubic_short}`
			case 'oilVolume': return `${value} ${t.liters_short}`
			case 'resource': return `${value} ${t.kilometers_short}`
			case 'boostPressure': return `${value} ${t.bars}`
			case 'profile': return `${value}%`
			case 'rimDiameter': return `${value} ${t.inches}`

			case 'capMaterial':
			case 'rotorMaterial':
			case 'blockMaterial':
				return t[`material_${value.toLowerCase()}` as keyof typeof t] ?? t.unknown

			case 'frontType':
			case 'rearType':
				return t[`brake_types_${value.toLowerCase()}` as keyof typeof t] ?? t.unknown
			case 'width':
			case 'rotorFront':
			case 'rotorRear':
				return `${value} ${t.millimeters_shortened}`

			case 'caliperPistonsFront':
			case 'gears':
			case 'cylinders':
			case 'caliperPistonsRear':
				return `${value} ${t.amounts_short}`

			case 'timingDrive': return t[value.toLowerCase() as keyof typeof t] ?? t.unknown
			case 'oilType': return value.replace("OIL_", "").replace("_", "-")
			case 'fuelType': return value.replace("_", "-")

			case 'pistonTravel':
			case 'cylinderDiameter':
				return `${value} ${t.millimeters_shortened}`

			default: return value
		}
	}

	const resolveStatus = () => {
		if (isResearched && isBought && isEquipped) {
			return <img src="/icons/24px/check.png" alt="checked" />
		} else if (isResearched && isBought) {
			return <p className="module-spec-footer-text">{t.module_researched_bought_unequipped}
			</p>
		} else if (isResearched && !isBought) {
			return <p className="module-spec-footer-text">{t.module_researched_not_bought}</p>
		} else if (!isResearched) {
			return <p className="module-spec-footer-text">{t.module_not_researched_not_bought}</p>
		}
	}

	const resolveDescription = () => {
		if (type === "TYRES") {
			const translationKey = `module_description_tyres_${name
				.toLowerCase()
				.replaceAll("/", "_")
				.replaceAll(" ", "_")
				.replaceAll("-", "_")
				.replaceAll(".", "_")}`
			return t[translationKey as keyof typeof t] ?? t.no_description
		}
		return t[`module_description_${moduleKey}` as keyof typeof t] ?? t.no_description
	}

	const resolveEffect = (effect: ModuleEffect) => {
		const moreThan2decimal = (num: number) => {
			if (num * 100 % 100) return true
			return false
		}
		const name = t[effect.param?.toLowerCase() as keyof typeof t] ?? effect.param?.toLowerCase()
		const value = moreThan2decimal(effect.value) ? effect.value.toFixed(2) : effect.value.toFixed(0)
		const valueSign = t[resolveTranslationKeyForValueSignBySpecType(effect.param as VehicleSpecParam) as keyof typeof t] ?? ''
		const change = effect.change
		const numberSign = change === 0 ? '' : change > 0 ? "+" : ""
		let effectClass = change === 0 ? '' : effect.positive ? 'positive' : 'negative'
		const changeStr = moreThan2decimal(change) ? change.toFixed(2) : change.toFixed(0)
		const shouldShowChange = change > 0 || change < 0
		console.log(effect.param, change, change > 0 || change < 0, shouldShowChange)
		return (
			<p className='module-spec-item-value'>• {name}: {value} {valueSign} {shouldShowChange && <><span className='module-spec-item-value'>|</span> <span className={`module-spec-item-change-value ${effectClass}`}>{numberSign}{changeStr} {valueSign}</span></>}</p>
		)
	}

	return (
		<div className='module-spec'>
			<div className='module-spec-header'>
				<div className='module-spec-image-container'>
					<img
						className='module-spec-image'
						src={`/icons/modules/all/${icon}.webp`}
						onError={(e) => {
							console.log(`Cannot load image ${icon} loading ${type.toLowerCase()} instead`)
							e.currentTarget.onerror = null
							e.currentTarget.src = `/icons/modules/${type.toLowerCase()}.png`
						}}
						alt='module'
					/>
				</div>
				<div className='module-spec-title'>
					<p className='module-spec-title-text'>{name}</p>
					<p className='module-spec-title-description-text'>{resolveTypeTranslation()}</p>
					<p className='module-spec-title-subtitle-text'>{t.level}: {level + 1}</p>
				</div>
			</div>

			<div className='module-spec-body'>
				{effects.length > 0 && <div className='module-spec-block-emphisized'>
					<div className='module-spec-block'>
						<p className='module-spec-subtitle'>{t.effects}</p>
						{effects.map((effect) => {
							return (<>
								<div className='module-spec-block-content'>
									{resolveEffect(effect)}
								</div>

							</>)
						})}
					</div>
				</div>
				}
				<div className='module-spec-block'>
					<p className='module-spec-price-content'>{t.price}: <span className='module-spec-price-xp'>{xpPrice}XP</span><span className='module-spec-price-xp-money-slash'> / </span><span className='module-spec-price-money'>{price}$</span></p>
				</div>
				<div className='module-spec-footer'>
					<p>{resolveStatus()}</p>
				</div>
			</div>

			<div className={`module-spec-hideable-block ${isAltHolded ? 'open' : ''}`}>
				<div className={`module-spec-hideable-block-inner ${isAltHolded ? 'open' : ''}`}>
					<div className='module-spec-block'>
						<p className='module-spec-subtitle'>{t.description}</p>
						<div className='module-spec-block-content additional'>
							<p>{resolveDescription()}</p>
						</div>
					</div>
					<div className='module-spec-block'>
						<p className='module-spec-subtitle'>{t.technical_characteristics}</p>
						<div className='module-spec-block-content additional'>
							{
								Object.entries(technicalCharacteristics).map(([key, value]) => (
									<p key={key}>• {t[key as keyof typeof t]}: <span className='module-spec-item-value'>{resolveTechnicalValue(key, value)}</span></p>
								))
							}
						</div>
					</div>
				</div>
			</div>
			<div className={`module-spec-hideable-block ${isAltHolded ? '' : 'open'}`}>
				<div className='module-spec-hideable-block-inner'>
					<div className='module-spec-hide-message-container'>
						<FullKeyButton
							text='Alt'
							keyboardKey='Alt'
							theme='dark'
						/>
						<p>{t.click_to_open_description}</p>
					</div>
				</div>
			</div>
		</div>
	)
}
